"use client"

import { useEffect, useState } from "react"
import { usePathname } from "next/navigation"
import { useUploadContext } from "@/lib/upload/upload-context"
import { hasLastUpload, retryLastUpload } from "@/lib/upload/upload-bridge"
import {
  CheckCircle2, XCircle, FileSpreadsheet, X, RotateCcw, Loader2, Mail,
} from "lucide-react"

// Stage timeline — same thresholds as components/dashboard/UploadStatusChip.tsx
// so the two surfaces (dashboard topbar chip vs floating card on
// landing) report identical progress copy. Lifted to keep the user
// oriented with a "✓ Validated" history rather than a shifting label,
// which is the move that makes this feel like Vercel deployments
// instead of a plain progress bar.
const STAGES = [
  { key: "upload",     label: "Uploaded",             threshold: 10  },
  { key: "validate",   label: "Parsed & validated",   threshold: 45  },
  { key: "duplicates", label: "Detecting duplicates", threshold: 75  },
  { key: "finalize",   label: "Finalizing",           threshold: 92  },
  { key: "done",       label: "Almost done",          threshold: 100 },
] as const

function stageStateFor(progress: number, idx: number) {
  const threshold = STAGES[idx].threshold
  const prev      = idx === 0 ? 0 : STAGES[idx - 1].threshold
  if (progress >= threshold) return "done" as const
  if (progress >= prev)      return "active" as const
  return "pending" as const
}

// Short single-line label used in the header of the floating card —
// kept distinct from the timeline below so the two reinforce each
// other rather than repeat the same word.
function phaseLabel(progress: number): string {
  if (progress < 10)  return "Uploading…"
  if (progress < 45)  return "Parsing & validating…"
  if (progress < 75)  return "Detecting duplicates…"
  if (progress < 92)  return "Finalizing…"
  return "Almost done…"
}

function formatElapsed(ms: number): string {
  if (ms < 1000)   return "now"
  const s = Math.floor(ms / 1000)
  if (s < 60)      return `${s}s`
  const m = Math.floor(s / 60)
  const rs = s % 60
  return rs === 0 ? `${m}m` : `${m}m ${rs}s`
}

export default function GlobalUploadStatus() {
  const pathname = usePathname()
  const {
    status,
    progress,
    fileName,
    startedAt,
    errorMessage,
    notice,
    dismissStatus,
  } = useUploadContext()

  // On the dashboard the topbar carries a richer popover for upload
  // progress (clickable chip → expandable panel). Suppress this floating
  // card there to avoid the duplication the user flagged. The card stays
  // for landing/marketing routes where there's no chrome to anchor to.
  const isDashboard = pathname?.startsWith("/dashboard") ?? false

  // Re-render every second while processing so the elapsed-time label
  // ticks up without needing a full context update on every tick.
  const [, forceTick] = useState(0)
  useEffect(() => {
    if (status !== "processing" || !startedAt) return
    const id = setInterval(() => forceTick((x) => x + 1), 1000)
    return () => clearInterval(id)
  }, [status, startedAt])

  if (status === "idle") return null
  if (isDashboard)       return null

  const shortName = fileName
    ? fileName.length > 28 ? fileName.slice(0, 25) + "…" : fileName
    : "your file"

  const elapsedText = startedAt ? formatElapsed(Date.now() - startedAt) : null

  return (
    <div className="fixed bottom-[calc(1.5rem+env(safe-area-inset-bottom))] right-4 sm:right-6 z-50 w-[calc(100%-2rem)] sm:w-[360px] max-w-sm pointer-events-none">
      <div
        className={`
          pointer-events-auto relative rounded-2xl border overflow-hidden
          shadow-2xl backdrop-blur-xl
          transition-all duration-300
          animate-in fade-in slide-in-from-bottom-3 duration-300
          ${status === "error"
            ? "border-[rgba(160,43,31,0.30)] bg-[#FFFFFF]/95 shadow-[rgba(160,43,31,0.15)]"
            : status === "done"
            ? "border-[rgba(15,81,50,0.25)] bg-[#FFFFFF]/95 shadow-[rgba(15,81,50,0.15)]"
            : "border-[rgba(10,10,10,0.10)] bg-[#FFFFFF]/95 shadow-[rgba(10,10,10,0.18)]"
          }
        `}
      >
        {/* Top shimmer line */}
        <div
          className={`absolute top-0 left-0 right-0 h-px ${
            status === "error" ? "bg-gradient-to-r from-transparent via-[#A02B1F]/60 to-transparent" :
            status === "done"  ? "bg-gradient-to-r from-transparent via-[#0F5132]/60 to-transparent" :
                                 "bg-gradient-to-r from-transparent via-[#1F7A4E]/60 to-transparent"
          }`}
        />

        <div className="px-4 py-3.5">

          {/* ── PROCESSING ── */}
          {status === "processing" && (
            <>
              <div className="flex items-start gap-3 mb-3">
                {/* Icon — pulsing ring */}
                <div className="relative flex-shrink-0 mt-0.5">
                  <div
                    className="absolute inset-0 rounded-lg bg-[#0F5132]/20 animate-ping"
                    style={{ animationDuration: "1.8s" }}
                  />
                  <div className="relative flex h-9 w-9 items-center justify-center rounded-lg bg-[#0F5132]/15 border border-[#0F5132]/25">
                    <FileSpreadsheet size={15} className="text-[#0F5132]" strokeWidth={1.75} />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-[12.5px] font-semibold text-[#0A0A0A] truncate leading-tight">{shortName}</p>
                  <div className="flex items-center gap-1.5 mt-1">
                    <Loader2 size={10} className="text-[#0F5132]/70 animate-spin flex-shrink-0" />
                    <span className="text-[11px] text-[rgba(10,10,10,0.55)]">{phaseLabel(progress)}</span>
                    {elapsedText && (
                      <>
                        <span className="text-[rgba(10,10,10,0.30)]">·</span>
                        <span className="text-[11px] text-[rgba(10,10,10,0.42)] tabular-nums">{elapsedText}</span>
                      </>
                    )}
                  </div>
                </div>

                <span className="text-[11px] font-semibold text-[#0F5132] tabular-nums flex-shrink-0 mt-0.5">
                  {Math.floor(progress)}%
                </span>
              </div>

              {/* Progress bar — glow + solid + shimmer */}
              <div className="relative h-1.5 rounded-full bg-[rgba(10,10,10,0.06)] overflow-hidden">
                <div
                  className="absolute top-0 left-0 h-full rounded-full bg-gradient-to-r from-[#1F7A4E] to-[#0F5132] blur-[3px] opacity-70 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
                <div
                  className="absolute top-0 left-0 h-full rounded-full bg-gradient-to-r from-[#1F7A4E] to-[#0F5132] transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
                <div
                  className="absolute top-0 h-full w-12 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"
                  style={{ left: `${progress - 10}%` }}
                />
              </div>

              {/* Stage timeline — same recipe as the dashboard chip so
                  users see Uploaded ✓ → Parsed ✓ → … instead of one
                  shifting label. Compact (5 rows × 22 px) so the
                  floating card stays comfortable on mobile too. */}
              <ul className="mt-3 space-y-1.5">
                {STAGES.map((stage, i) => {
                  const state = stageStateFor(progress, i)
                  return (
                    <li
                      key={stage.key}
                      className={`
                        flex items-center gap-2 text-[11px] leading-tight
                        transition-colors duration-300
                        ${state === "done"   ? "text-[rgba(10,10,10,0.65)]"
                          : state === "active" ? "text-[#0A0A0A] font-semibold"
                                               : "text-[rgba(10,10,10,0.38)]"}
                      `}
                    >
                      <span className="flex-shrink-0 flex items-center justify-center w-3.5 h-3.5">
                        {state === "done" && (
                          <span className="flex h-3 w-3 items-center justify-center rounded-full bg-[#0F5132]/20 border border-[#0F5132]/40">
                            <svg viewBox="0 0 12 12" fill="none" className="w-1.5 h-1.5">
                              <path d="M2.5 6.5L5 9L9.5 3.5" stroke="#0F5132" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </span>
                        )}
                        {state === "active" && (
                          <span className="relative flex h-3 w-3 items-center justify-center">
                            <span className="absolute inset-0 rounded-full bg-[#0F5132]/20 animate-ping" style={{ animationDuration: "1.5s" }} />
                            <span className="relative h-2 w-2 rounded-full bg-[#0F5132]" />
                          </span>
                        )}
                        {state === "pending" && (
                          <span className="h-2.5 w-2.5 rounded-full border border-[rgba(10,10,10,0.18)]" />
                        )}
                      </span>
                      {stage.label}
                    </li>
                  )
                })}
              </ul>

              {/* Notice line — replaces the separate top toast for
                  things like "We'll email you when done" or sample-run
                  callouts. One card, one place to look. */}
              {notice && (
                <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-[rgba(10,10,10,0.06)]">
                  <Mail size={11} className="text-[rgba(10,10,10,0.45)] flex-shrink-0" />
                  <span className="text-[11px] text-[rgba(10,10,10,0.55)] truncate">{notice}</span>
                </div>
              )}
            </>
          )}

          {/* ── DONE ── */}
          {status === "done" && (
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 flex h-9 w-9 items-center justify-center rounded-lg bg-[#0F5132]/15 border border-[#0F5132]/25">
                <CheckCircle2 size={16} className="text-[#0F5132]" strokeWidth={2} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[12.5px] font-semibold text-[#0F5132] leading-tight">Dataset ready</p>
                <p className="text-[11px] text-[rgba(10,10,10,0.45)] mt-0.5 truncate">
                  <span className="text-[rgba(10,10,10,0.65)]">{shortName}</span> has been cleaned
                  {elapsedText && <> <span className="text-[rgba(10,10,10,0.30)]">·</span> <span className="tabular-nums">{elapsedText}</span></>}
                </p>
              </div>
              <button
                onClick={dismissStatus}
                title="Dismiss"
                className="flex-shrink-0 p-1.5 rounded-md text-[rgba(10,10,10,0.42)] hover:text-[rgba(10,10,10,0.70)] hover:bg-[rgba(10,10,10,0.06)] transition-all duration-150"
              >
                <X size={14} />
              </button>
            </div>
          )}

          {/* ── ERROR ── */}
          {status === "error" && (
            <>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 flex h-9 w-9 items-center justify-center rounded-lg bg-[rgba(160,43,31,0.10)] border border-[rgba(160,43,31,0.25)] mt-0.5">
                  <XCircle size={16} className="text-[#A02B1F]" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12.5px] font-semibold text-[#A02B1F] leading-tight">Upload failed</p>
                  <p className="text-[11px] text-[rgba(10,10,10,0.62)] mt-0.5 leading-relaxed">
                    {errorMessage || "Something went wrong processing your file."}
                  </p>
                </div>
                <button
                  onClick={dismissStatus}
                  title="Dismiss"
                  className="flex-shrink-0 p-1.5 rounded-md text-[rgba(10,10,10,0.42)] hover:text-[rgba(10,10,10,0.70)] hover:bg-[rgba(10,10,10,0.06)] transition-all duration-150"
                >
                  <X size={14} />
                </button>
              </div>

              {hasLastUpload() && (
                <div className="mt-3 flex justify-end">
                  <button
                    onClick={() => {
                      dismissStatus()
                      // tiny delay so the reset-to-idle flush completes
                      // before we spin up the new upload status
                      setTimeout(() => retryLastUpload(), 50)
                    }}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold border border-[rgba(160,43,31,0.30)] bg-[rgba(160,43,31,0.06)] text-[#A02B1F] hover:bg-[rgba(160,43,31,0.12)] hover:border-[rgba(160,43,31,0.50)] transition-all duration-150"
                  >
                    <RotateCcw size={11} strokeWidth={2.25} />
                    Retry upload
                  </button>
                </div>
              )}
            </>
          )}

        </div>
      </div>
    </div>
  )
}
